import React from 'react';
import { Box, Typography, Stack, Tooltip, useTheme } from '@mui/material';

const DAYS_OF_WEEK = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];
const MONTHS = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const parseLocalDateTime = (dateString, timeString = "00:00") => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  const [hour = 0, minute = 0] = String(timeString || "00:00").split(":").map(Number);
  return new Date(year, month - 1, day, hour, minute);
};

const formatTime = (timeString) => {
  if (!timeString) return "";
  return String(timeString).slice(0, 5);
};

const formatTimeRange = (event) => {
  if (!event.horaInicio) return "Todo el día";
  if (!event.horaFin) return formatTime(event.horaInicio);
  return `${formatTime(event.horaInicio)} - ${formatTime(event.horaFin)}`;
};

export default function ResultSearch({ events, searchTerm, onEventClick }) {
  const theme = useTheme();
  const now = new Date();

  // Filtrar eventos por término de búsqueda (ignorando mayúsculas/minúsculas)
  const filteredEvents = events
    .filter((event) => (event.titulo || "").toLowerCase().includes(searchTerm.toLowerCase()))
    .map((event) => {
      const startDate = parseLocalDateTime(event.fecha, event.horaInicio);
      const endDate = parseLocalDateTime(event.endDate || event.fecha, event.horaFin || event.horaInicio || "23:59");
      if (
        startDate &&
        endDate &&
        event.horaInicio &&
        event.horaFin &&
        formatTime(event.horaFin) < formatTime(event.horaInicio) &&
        endDate <= startDate
      ) {
        endDate.setDate(endDate.getDate() + 1);
      }

      return {
        event,
        startDate,
        endDate,
        isPast: endDate ? endDate < now : false,
      };
    });

  const upcomingEvents = filteredEvents
    .filter((item) => !item.isPast)
    .sort((a, b) => a.startDate - b.startDate);

  const pastEvents = filteredEvents
    .filter((item) => item.isPast)
    .sort((a, b) => b.startDate - a.startDate);

  const getFormattedDateParts = (dateString) => {
    if (!dateString) return { day: "", rest: "" };
    const [y, m, d] = dateString.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    
    const day = String(d);
    const month = MONTHS[m - 1];
    const year = String(y);
    const dayOfWeek = DAYS_OF_WEEK[dateObj.getDay()];
    
    return {
      day: day,
      rest: `${month} de ${year}, ${dayOfWeek}`
    };
  };

  const renderRow = ({ event: evt }, index) => {
    const eventColor = evt.color || theme.palette.primary.main;
    const { day, rest } = getFormattedDateParts(evt.fecha);

    return (
      <Box
        key={`${evt.id || evt.titulo || "evento"}-${index}`}
        onClick={() => onEventClick && onEventClick(evt)}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "42px 1fr", md: "44px minmax(150px, 1.1fr) 18px minmax(130px, 0.8fr) minmax(180px, 1.5fr)" },
          columnGap: { xs: 1, md: 2 },
          rowGap: 0.75,
          alignItems: "center",
          minHeight: 42,
          px: 1,
          py: 0.55,
          borderBottom: "2px solid #27272f",
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Typography sx={{ fontSize: 28, fontWeight: 500, lineHeight: 1 }}>
          {day}
        </Typography>

        <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {rest}
        </Typography>

        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: eventColor,
            justifySelf: { xs: "start", md: "center" },
            gridColumn: { xs: "1", md: "auto" },
          }}
        />

        <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", gridColumn: { xs: "2", md: "auto" } }}>
          {formatTimeRange(evt)}
        </Typography>

        <Tooltip title={evt.titulo || ""} arrow enterDelay={350}>
          <Typography tabIndex={0} sx={{ fontSize: 14, fontWeight: 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", gridColumn: { xs: "1 / -1", md: "auto" } }}>
            {evt.titulo}
          </Typography>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 0, maxHeight: { xs: 520, lg: "calc(100vh - 210px)" } }}>
      {filteredEvents.length > 0 ? (
        <Box sx={{ bgcolor: "background.paper", minHeight: "100%", borderTop: "2px solid #27272f", width: "100%" }}>
          <Stack spacing={0}>
            {upcomingEvents.map((item, index) => renderRow(item, index))}

            {pastEvents.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1,
                  py: 1.5,
                  color: "text.secondary",
                }}
              >
                <Box sx={{ height: "1px", bgcolor: "#606071", flex: 1 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
                  Eventos anteriores
                </Typography>
                <Box sx={{ height: "1px", bgcolor: "#606071", flex: 1 }} />
              </Box>
            )}

            {pastEvents.map((item, index) => renderRow(item, index))}
          </Stack>
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
          No se encontraron eventos para "{searchTerm}"
        </Typography>
      )}
    </Box>
  );
}
